import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
  sublabel?: string;
  badge?: string;
}

interface Props {
  options: Option[];
  value: string;
  onChange: (value: string, option?: Option) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export default function SearchableSelect({
  options, value, onChange, placeholder = 'Select...', searchPlaceholder = '🔍 Type to search...',
  required, disabled, className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.value === value);

  const filtered = query.trim()
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        o.sublabel?.toLowerCase().includes(query.toLowerCase()) ||
        o.badge?.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false); setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(o => !o); setQuery(''); }}}
        className={`input w-full text-left flex items-center justify-between gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={selected ? 'text-slate-800' : 'text-slate-400'}>
          {selected ? selected.label : placeholder}
        </span>
        {selected?.badge && (
          <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-auto">{selected.badge}</span>
        )}
        <span className="text-slate-400 text-xs flex-shrink-0">{open ? '▲' : '▼'}</span>
      </button>
      {required && !value && <input tabIndex={-1} required style={{opacity:0,height:0,position:'absolute'}} />}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-slate-100">
            <input
              ref={inputRef}
              className="w-full text-sm px-3 py-1.5 border border-slate-200 rounded-lg outline-none focus:border-blue-400"
              placeholder={searchPlaceholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          {/* Options list */}
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-center text-slate-400 text-xs py-4">No results for "{query}"</p>
            ) : (
              filtered.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value, opt); setOpen(false); setQuery(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2 transition-colors ${opt.value === value ? 'bg-blue-50 font-semibold text-[#02357A]' : 'text-slate-700'}`}
                >
                  <div>
                    <span>{opt.label}</span>
                    {opt.sublabel && <span className="block text-xs text-slate-400">{opt.sublabel}</span>}
                  </div>
                  {opt.badge && <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded flex-shrink-0">{opt.badge}</span>}
                </button>
              ))
            )}
          </div>
          {/* Clear */}
          {value && (
            <div className="p-1.5 border-t border-slate-100">
              <button type="button" onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                className="w-full text-xs text-red-500 hover:text-red-700 py-1">✕ Clear selection</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
